import { Button } from "@/ui-components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/ui-components/ui/card";
import { Loader2, CheckCircle2, Flame } from "lucide-react";
import { useState, useEffect } from "react";
import { useBillingCreateCheckout } from "@/lib/api/requests/billing.requests";
import { annotateActiveSpan, withTelemetrySpan } from "@/lib/monitoring/telemetry";
import { toast } from "@/lib/toast";
import { useSubscriptions } from "@/lib/hooks/use-subscriptions";
import { useFetchUserInfo } from "@/lib/api/requests/user-profile.requests";
import { useNavigate } from "react-router-dom";
import { ErrorEntity } from "@/lib/utils/error-entities";
import {
  createMetaEventId,
  getMetaBrowserIds,
  trackMetaCustomEvent,
  trackMetaStandardEvent,
} from "@/lib/meta-pixel/meta-pixel";

const PLAN_CONFIG = {
  basic: {
    label: "Basic",
    value: 29,
  },
  pro: {
    label: "Pro Abo",
    value: 59,
  },
} as const;

const TRIAL_BADGE = (
  <div className="mt-3 inline-flex items-center justify-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
    14 Tage kostenlos testen
  </div>
);

export default function ChoosePlanPage() {
  const [isLoadingBasic, setIsLoadingBasic] = useState(false);
  const [isLoadingPro, setIsLoadingPro] = useState(false);
  const { mutateAsync: createCheckout } = useBillingCreateCheckout();
  const { hasUsedTrial } = useSubscriptions();
  const { fetchUserInfo } = useFetchUserInfo();
  const navigate = useNavigate();

  const isExpectedChoosePlanError = (error: unknown) => {
    if (!error || typeof error !== "object") {
      return false;
    }

    const entity = "entity" in error ? error.entity : undefined;
    const statusCode = "statusCode" in error ? error.statusCode : undefined;

    return entity === ErrorEntity.NoBillingContractForbidden || statusCode === 403;
  };

  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        await fetchUserInfo();
        // If request succeeds, user has an active plan (no 402 exception)
        navigate("/");
      } catch (error) {
        // User still needs to choose a plan, stay on page
        if (isExpectedChoosePlanError(error)) {
          console.info("User still needs to choose a plan");
          return;
        }

        console.error("Unexpected choose-plan status check error:", error);
      }
    };

    checkUserStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    trackMetaCustomEvent("PlanSelectionViewed", {
      has_trial_available: !hasUsedTrial,
    });
  }, [hasUsedTrial]);

  const handleCheckout = async (plan: 'basic' | 'pro') => {
    try {
      if (plan === 'basic') setIsLoadingBasic(true);
      if (plan === 'pro') setIsLoadingPro(true);

      const metaEventId = createMetaEventId();
      const { fbp, fbc } = getMetaBrowserIds();
      const planConfig = PLAN_CONFIG[plan];

      await withTelemetrySpan(
        "billing.checkout.start",
        "feature.billing.checkout",
        {
          "billing.plan": plan,
          "billing.plan_label": planConfig.label,
          "billing.has_trial_available": !hasUsedTrial,
        },
        async () => {
          annotateActiveSpan({}, {
            "billing.checkout.value": { value: planConfig.value, unit: "none" },
          });

          const response = await createCheckout({
            plan,
            metaEventId,
            fbp,
            fbc,
            metaSourceUrl: window.location.href,
          });
          
          if (response?.url) {
            annotateActiveSpan({
              "billing.checkout.redirect_ready": true,
            });
            trackMetaStandardEvent("InitiateCheckout", {
              currency: "EUR",
              value: planConfig.value,
              content_name: planConfig.label,
              content_category: "subscription",
              contents: [
                {
                  id: plan,
                  quantity: 1,
                  item_price: planConfig.value,
                },
              ],
              num_items: 1,
              has_trial_available: !hasUsedTrial,
            }, { eventId: metaEventId });
            window.location.href = response.url;
          } else {
            throw new Error('No checkout URL returned.');
          }
        },
      );
    } catch (error) {
      console.error('Checkout error:', error);
      annotateActiveSpan({
        "billing.checkout.success": false,
      });
      toast.error('Fehler beim Starten des Checkout', {
        description: 'Bitte versuche es später noch einmal oder kontaktiere den Support.',
        error,
      });
      if (plan === 'basic') setIsLoadingBasic(false);
      if (plan === 'pro') setIsLoadingPro(false);
    }
  };

  return (
    <div className="h-screen w-full bg-slate-50/30 flex flex-col items-center py-12 px-4 sm:px-8 overflow-y-auto">
      <div className="w-full max-w-4xl space-y-10 animate-in fade-in duration-700 slide-in-from-bottom-4 flex flex-col items-center">
        
        {/* Header Section */}
        <div className="flex flex-col items-center text-center gap-6">
           <img src="/logo.png" alt="SportyPost Logo" className="h-10 md:h-12 object-contain opacity-80" />
           
           <div className="space-y-4 max-w-2xl">
             <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
               Wähle deinen Plan 🚀
             </h1>
             <p className="text-lg text-slate-500 max-w-xl mx-auto">
               Starte jetzt mit SportyPost und erstelle atemberaubende Social Media Posts für deinen Verein.
             </p>
           </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
          {/* Standard Plan Card */}
          <Card className="border-none shadow-xl shadow-slate-200/40 bg-white ring-1 ring-slate-200/60 rounded-2xl overflow-hidden flex flex-col h-full">
            <CardHeader className="text-center pt-8 pb-6 border-b border-slate-50">
              <CardTitle className="text-xl font-semibold text-slate-900 mb-2">Basic</CardTitle>
              <div className="flex justify-center items-end text-slate-900">
                <span className="text-4xl font-bold tracking-tight">29</span>
                <span className="text-lg font-medium text-slate-500 ml-1 mb-1">€/mtl.</span>
              </div>
              <CardDescription className="text-slate-500 mt-2">
                Für Einsteiger.
              </CardDescription>
              {!hasUsedTrial && TRIAL_BADGE}
            </CardHeader>
            
            <CardContent className="p-6 md:p-8 space-y-6 flex-1">
              <ul className="space-y-4">
                {[
                  "Unbegrenzte Social Media Posts",
                  "Zugriff auf alle Design-Templates",
                  "Posten auf Instagram, Facebook & WhatsApp",
                  "Planung & Auto-Posting",
                  "Sponsoren-Management"
                ].map((feature, i) => (
                  <li key={i} className="flex items-start text-slate-600 text-sm">
                    <CheckCircle2 className="w-5 h-5 text-slate-400 mr-3 shrink-0" />
                    <span className="leading-tight pt-0.5">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
            
            <CardFooter className="p-6 md:p-8 pt-0 mt-auto">
              <Button 
                variant="outline"
                className="w-full h-11 rounded-xl border-slate-200 hover:bg-slate-50 text-slate-900 font-medium transition-all"
                onClick={() => handleCheckout('basic')}
                disabled={isLoadingBasic || isLoadingPro}
              >
                {isLoadingBasic ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Leite weiter...
                  </>
                ) : (
                  hasUsedTrial ? "Basic wählen" : "Basic kostenlos testen"
                )}
              </Button>
            </CardFooter>
          </Card>

          {/* Pro Plan Card */}
          <Card className="border-none shadow-xl shadow-blue-900/20 bg-white ring-2 ring-blue-600 rounded-2xl overflow-hidden flex flex-col h-full md:scale-105 relative">
            <div className="absolute top-4 right-4 bg-blue-600 text-white text-[10px] font-bold uppercase py-1 px-2.5 rounded-full shadow-sm tracking-wide z-10 flex items-center gap-1">
              <Flame className="w-3 h-3 fill-white" />
              Beliebt
            </div>
            
            <CardHeader className="text-center pt-8 pb-6 border-b border-slate-50 bg-blue-50/30">
              <CardTitle className="text-xl font-semibold text-blue-900 mb-2">Pro Abo</CardTitle>
              <div className="flex justify-center items-end text-blue-600">
                <span className="text-4xl font-bold tracking-tight">59</span>
                <span className="text-lg font-medium text-blue-600/80 ml-1 mb-1">€/mtl.</span>
              </div>
              {!hasUsedTrial && TRIAL_BADGE}
            </CardHeader>
            
            <CardContent className="p-6 md:p-8 space-y-6 flex-1">
              <ul className="space-y-4">
                <li className="flex items-start text-slate-900 font-medium text-sm">
                  <span>Alles im Basic-Paket</span>
                </li>
                {[
                  "Eigene Templates erstellen und bearbeiten",
                  "Posts ohne Wasserzeichen",
                  "KI Bildunterschrift",
                  "KI Hintergrund-Entfernung",
                  <span key="analytics" className="text-slate-500">Analytics & Insights <span className="text-[10px] font-bold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded ml-1 uppercase tracking-wide">Bald</span></span>
                ].map((feature, i) => (
                  <li key={i} className="flex items-start text-slate-600 text-sm">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 mr-3 shrink-0" />
                    <span className="leading-tight pt-0.5">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
            
            <CardFooter className="p-6 md:p-8 pt-0 mt-auto">
              <Button 
                className="w-full h-11 text-base font-medium shadow-lg shadow-blue-500/25 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
                onClick={() => handleCheckout('pro')}
                disabled={isLoadingBasic || isLoadingPro}
              >
                {isLoadingPro ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Leite weiter...
                  </>
                ) : (
                  hasUsedTrial ? "Pro Abo wählen" : "Pro kostenlos testen"
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>
        
        <p className="text-center text-xs text-gray-400 mt-12 max-w-lg">
          Du wirst sicher zu Stripe weitergeleitet, um deine Zahlungsdetails zu hinterlegen. { !hasUsedTrial && "Testzeitraum per Klick kündbar." }
        </p>
      </div>
    </div>
  );
}
